import {
  filters,
  API_INFINITY_VALUE,
  FILTER_TYPE,
} from '../../../../../constants';
import type { CustomCompanyFilter } from './types';
import { dots } from '../../../../../functions';
import { ruleToValueRangeTransformation, valueResolver } from './utils';

const titleFormatter = ([_, __, min, max]: (number | string)[]) =>
  `is between ${min} and ${max === API_INFINITY_VALUE ? '∞' : max}`;

export const generateValueFilters = (): CustomCompanyFilter[] => [
  // Intrinsic Value
  {
    id: filters.intrinsicDiscount,
    label: 'Share Price vs Fair Value',
    subLabel: '(Discount to Discounted Cash Flow value)',
    labels: {
      ...dots(100, { start: -50, step: 25 }),
      '-55': '-∞',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.intrinsicDiscount}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.intrinsicDiscount}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -55;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -55,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -50,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -50
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // Discount to Analyst Price Target
  {
    id: filters.analyst_target_discount,
    label: 'Share Price vs Analyst Price Target',
    subLabel: '(Discount to Analyst Price Target)',
    labels: {
      ...dots(100, { start: -50, step: 25 }),
      '-55': '-∞',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.analyst_target_discount}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.analyst_target_discount}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -55;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -55,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -50,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -50
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  // PE Ratio
  {
    defaultValue: `["${filters.pe}", "between", 0, 10]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    rule: (range) => `["${filters.pe}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.pe,
    label: 'PE Ratio',
    subLabel: '(Price to Earnings Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter,
  },
  // Price to Sales Ratio
  {
    defaultValue: `["${filters.price_to_sales}", "between", 5, 20]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    rule: (range) =>
      `["${filters.price_to_sales}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.price_to_sales,
    label: 'PS Ratio',
    subLabel: '(Price to Sales Ratio)',
    labels: {
      ...dots(100, { step: 5 }),
      0: '0',
      20: '20',
      50: '50',
      100: '100',
      105: '∞',
    },
    max: 105,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter,
  },
  // PEG Ratio
  {
    defaultValue: `["${filters.peg}", "between", 0.5, 10]`,
    ruleToValue: ([_, __, ...range]) => {
      return [...ruleToValueRangeTransformation(range)];
    },
    rule: (range) => `["${filters.peg}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.peg,
    label: 'PEG Ratio',
    subLabel: '(Price to Earnings-to-growth Ratio)',
    labels: {
      ...dots(30, { step: 2 }),
      0: '0',
      10: '1',
      28: '10',
      30: '∞',
    },
    max: 30,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      return [...valueResolver(range, API_INFINITY_VALUE)];
    },
    titleFormatter,
  },
  // Price to Book
  {
    defaultValue: `["${filters.pb}", "between", 0.5, 10]`,
    ruleToValue: ([_, __, ...range]) => {
      return [...ruleToValueRangeTransformation(range)];
    },
    rule: (range) => `["${filters.pb}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.pb,
    label: 'PB Ratio',
    subLabel: '(Price to Book Ratio)',
    labels: { ...dots(28), 0: '0', 10: '1', 28: '10', 30: '∞' },
    max: 30,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      return [...valueResolver(range, API_INFINITY_VALUE)];
    },
    titleFormatter,
  },
  // (Price to Free Cash Flow Ratio)
  {
    defaultValue: `["${filters.price_to_fcf}", "between", 0, 10]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    rule: (range) =>
      `["${filters.price_to_fcf}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.price_to_fcf,
    label: 'PFCF Ratio',
    subLabel: '(Price to Free Cash Flow Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter,
  },
  // (Enterprise Value to EBITDA Ratio)
  {
    defaultValue: `["${filters.value_ev_to_ebitda}", "between", 0, 10]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    rule: (range) =>
      `["${filters.value_ev_to_ebitda}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.value_ev_to_ebitda,
    label: 'EV to Ebitda',
    subLabel: '(Enterprise Value to EBITDA Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range: [number, number]) => {
      const [min, max] = range;
      const maxValue = 50;
      return [min, max > maxValue ? API_INFINITY_VALUE : max];
    },
    titleFormatter,
  },
  // (Enterprise Value to Sales Ratio)
  {
    defaultValue: `["${filters.value_ev_to_sales}", "between", 0, 10]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    rule: (range) =>
      `["${filters.value_ev_to_sales}", "between", ${range[0]}, ${range[1]}]`,
    id: filters.value_ev_to_sales,
    label: 'EV to Sales',
    subLabel: '(Enterprise Value to Sales Ratio)',
    labels: { ...dots(50, { step: 5 }), 0: '0', 20: '20', 50: '50', 55: '∞' },
    max: 55,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range: [number, number]) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter,
  },
  // Analyst Count
  {
    id: filters.exactAnalystCount,
    label: 'Analyst Count',
    subLabel: '(Number of analysts covering the stock)',
    labels: {
      ...dots(60, { start: 0, step: 5 }),
      0: '0',
      10: '10',
      20: '20',
      30: '30',
      40: '40',
      50: '50',
      60: '60',
    },
    defaultValue: `["${filters.exactAnalystCount}", "between", 0, 3]`,
    rule: (range) =>
      `["${filters.exactAnalystCount}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    min: 0,
    max: 60,
    type: FILTER_TYPE.RANGE,
    softMin: 0,
    softMax: 60,
    valueResolver: (range) => range.map((value) => value),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else {
        return `is between ${min} and ${max}`;
      }
    },
  },
];
